/**
 * Centralized Free vs Plus entitlements (client mirror of server rules).
 *
 * This is the single place the UI should ask "can this user do X?". It mirrors
 * the server-authoritative checks in `functions/entitlements.py` and
 * `firestore.rules`. The backend ALWAYS re-enforces these rules — the client
 * copy exists only to gate UI early and show clean upgrade CTAs.
 *
 * Rules:
 *  - Free users: send/accept normal (live) challenges, join Open Arena,
 *    basic chat/social, join up to the free group limit. Platform fee 5%.
 *  - Plus users: everything Free has, plus create/send/accept Custom
 *    Challenges, unlimited groups. Platform fee 2%.
 *  - Custom Challenges require BOTH sender and receiver to be Plus.
 *
 * IMPORTANT (platform fee): the client NEVER decides the authoritative fee.
 * `getPlatformFeeRate()` here returns an ESTIMATE only; the server computes the
 * final rate from the winner's subscription at settlement.
 */

import { hasLockedInPlus, getEstimatedFeeRate, FEE_RATE_FREE, FEE_RATE_PLUS } from './platformFee';
import { MAX_FREE_GROUP_MEMBERSHIPS } from '../constants/groupLimits';

export const CHALLENGE_TYPE = Object.freeze({
  LIVE: 'live',
  CUSTOM: 'custom',
});

/** True when the given profile/user doc has an active or trialing subscription. */
export function hasPlus(user) {
  return hasLockedInPlus(user);
}

/** A challenge is "custom" via challengeType or matchup.isCustom (mirrors rules). */
export function isCustomChallenge(challenge) {
  if (!challenge) return false;
  if (challenge.challengeType === CHALLENGE_TYPE.CUSTOM) return true;
  if (challenge.matchup && challenge.matchup.isCustom === true) return true;
  return false;
}

/** Normalize a challenge type string; unknown/empty -> 'live'. */
export function normalizeChallengeType(challengeType, challenge) {
  if (challengeType === CHALLENGE_TYPE.CUSTOM) return CHALLENGE_TYPE.CUSTOM;
  if (challengeType === CHALLENGE_TYPE.LIVE) return CHALLENGE_TYPE.LIVE;
  return isCustomChallenge(challenge) ? CHALLENGE_TYPE.CUSTOM : CHALLENGE_TYPE.LIVE;
}

/** Plus-only: create/send a Custom Challenge. */
export function canCreateCustomChallenge(user) {
  return hasPlus(user);
}

/**
 * Can this user accept the given challenge?
 *  - Normal (live) challenge: always allowed (registration handled elsewhere).
 *  - Custom challenge: receiver must be Plus.
 */
export function canAcceptCustomChallenge(user, challenge) {
  if (!isCustomChallenge(challenge)) return true;
  return hasPlus(user);
}

/**
 * Can `user` send a challenge of `challengeType` to `recipientUser`?
 *  - live: allowed (other gates like registration handled by caller).
 *  - custom: sender must be Plus AND, when a specific recipient is known,
 *    the recipient must also be Plus. For open challenges with no specific
 *    recipient, pass `recipientUser = undefined` — the acceptor's Plus status
 *    is enforced at accept time.
 */
export function canSendChallenge(user, challengeType, recipientUser) {
  const type = normalizeChallengeType(challengeType);
  if (type !== CHALLENGE_TYPE.CUSTOM) return true;
  if (!canCreateCustomChallenge(user)) return false;
  if (recipientUser === undefined || recipientUser === null) return true;
  return hasPlus(recipientUser);
}

/**
 * Why a custom send is blocked, for choosing the right copy. Returns:
 *  - null            -> allowed (or not a custom challenge)
 *  - 'sender_plus'   -> sender needs Plus
 *  - 'recipient_plus'-> recipient needs Plus
 */
export function customSendBlockReason(user, challengeType, recipientUser) {
  const type = normalizeChallengeType(challengeType);
  if (type !== CHALLENGE_TYPE.CUSTOM) return null;
  if (!hasPlus(user)) return 'sender_plus';
  if (recipientUser != null && !hasPlus(recipientUser)) return 'recipient_plus';
  return null;
}

/**
 * ESTIMATE ONLY — never authoritative. Server decides the final fee at
 * settlement based on the winner's subscription. Use only for pre-settlement
 * UI labeled as estimated.
 */
export function getPlatformFeeRate(user) {
  return getEstimatedFeeRate(user);
}

/** Max group/league memberships. Plus = unlimited (Infinity). */
export function getGroupLimit(user) {
  return hasPlus(user) ? Infinity : MAX_FREE_GROUP_MEMBERSHIPS;
}

/** Can this user create a private group/league? (Plus-only) */
export function canCreateGroup(user) {
  return hasPlus(user);
}

export { FEE_RATE_FREE, FEE_RATE_PLUS, MAX_FREE_GROUP_MEMBERSHIPS };

/* ---------------------------------------------------------------------------
 * Upgrade copy — every blocked Plus action should surface an upgrade moment,
 * not a raw error. Use "challenge", "custom challenge", "Plus", "funds",
 * "platform fee". Never sportsbook language (bet/wager/odds/escrow/winnings).
 * ------------------------------------------------------------------------- */

export const ENTITLEMENT_COPY = Object.freeze({
  // Sender is not Plus and tries to create/send a custom challenge.
  customSenderNeedsPlus: {
    title: 'Custom Challenges are a Plus feature',
    body: 'Upgrade to LockedIn Plus to create and send custom challenges. Plus also lowers your platform fee to 2%.',
    cta: 'Upgrade to Plus',
  },
  // Sender is Plus but the chosen recipient is not.
  customRecipientNeedsPlus: {
    title: 'This player needs LockedIn Plus',
    body: 'Custom Challenges are available between Plus members. Send a regular challenge instead, or invite them to upgrade.',
    cta: 'Send regular challenge',
  },
  // Receiver opens a custom challenge they cannot accept yet.
  customAcceptNeedsPlus: {
    title: 'This is a Custom Challenge',
    body: 'Upgrade to LockedIn Plus to accept custom challenges between members.',
    cta: 'Upgrade to Plus',
  },
});

/** Pick the right upgrade copy for a blocked custom send. */
export function getCustomSendUpgradeCopy(reason) {
  return reason === 'recipient_plus'
    ? ENTITLEMENT_COPY.customRecipientNeedsPlus
    : ENTITLEMENT_COPY.customSenderNeedsPlus;
}
